import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useModalLayerPresence } from '../../hooks/useModalLayerPresence'
import ModalShell from './ModalShell'

type Props = {
  isOpen: boolean
  title: string
  description?: string
  /** Rendered next to the close control (e.g. kebab menu). */
  headerExtra?: ReactNode
  maxWidthClassName?: string
  onClose: () => void
  children: ReactNode
  /**
   * Default: bottom sheet on narrow viewports (full-width), centered from `sm` up.
   * When true: centered dialog with padding on all sizes — use for dense or tall content on phones.
   */
  mobileCentered?: boolean
}

const EXIT_MS = 150

export default function Modal({
  isOpen,
  title,
  description,
  headerExtra,
  maxWidthClassName = 'max-w-md',
  onClose,
  children,
  mobileCentered = false,
}: Props) {
  const [visible, setVisible] = useState(isOpen)
  const [phase, setPhase] = useState<'enter' | 'exit' | 'idle'>(isOpen ? 'enter' : 'idle')
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useLayoutEffect(() => {
    if (isOpen) {
      if (exitTimer.current) {
        clearTimeout(exitTimer.current)
        exitTimer.current = null
      }
      setVisible(true)
      setPhase('enter')
    } else if (visible) {
      setPhase('exit')
      exitTimer.current = setTimeout(() => {
        setVisible(false)
        setPhase('idle')
        exitTimer.current = null
      }, EXIT_MS)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (exitTimer.current) clearTimeout(exitTimer.current)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [isOpen])

  useModalLayerPresence(isOpen)

  const mouseDownTarget = useRef<EventTarget | null>(null)

  if (!visible) return null

  const backdropLayout = mobileCentered
    ? 'items-center justify-center p-4'
    : 'flex-col items-center justify-end p-0 sm:flex-row sm:items-center sm:justify-center sm:p-4'

  const safeArea =
    'pt-[max(1.75rem,calc(env(safe-area-inset-top,0px)+0.75rem))] pb-[max(1.25rem,calc(env(safe-area-inset-bottom,0px)+0.75rem))] pl-[max(1rem,calc(env(safe-area-inset-left,0px)+0.25rem))] pr-[max(1rem,calc(env(safe-area-inset-right,0px)+0.25rem))]'

  const animClass = phase === 'exit' ? 'modal-backdrop-exit' : 'modal-backdrop-enter'
  const shellAnimClass = phase === 'exit' ? 'modal-shell-exit' : 'modal-shell-enter'

  const modal = (
    <div
      className={`modal-backdrop fixed inset-0 z-[110] flex ${backdropLayout} ${safeArea} ${animClass}`}
      onMouseDown={(e) => {
        mouseDownTarget.current = e.target
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
          onClose()
        }
        mouseDownTarget.current = null
      }}
      role="dialog"
      aria-modal="true"
    >
      <ModalShell
        className={`w-full min-h-0 ${maxWidthClassName} ${mobileCentered ? 'max-h-[min(95vh,100dvh)]' : ''} sm:max-h-[min(90vh,100dvh)] flex flex-col overflow-hidden ${shellAnimClass}`}
      >
        <div className="modal-header p-4 sm:p-6 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="modal-title text-lg font-semibold truncate">{title}</h2>
            {description ? <p className="modal-description text-sm mt-1">{description}</p> : null}
          </div>
          <div className="flex items-start gap-1 flex-shrink-0">
            {headerExtra}
            <button
              type="button"
              onClick={onClose}
              className="modal-close flex-shrink-0"
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        </div>
        <div className="px-4 pb-5 pt-0 sm:px-6 sm:pb-6 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      </ModalShell>
    </div>
  )

  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}
